import { createContext, useState, useEffect, useCallback, useRef } from 'react';
import React from 'react';
import apiClient from '../utils/apiClient';
import { toast } from 'react-toastify';
import { encryptMnemonic, decryptMnemonic } from '../utils/clientCrypto';
import { deriveAddressesFromMnemonic } from '../utils/browserSigner';
import { getBrowserFingerprint } from '../utils/fingerprint';

export const WalletContext = createContext();

export function WalletProvider({ children }) {
  const [wallet, setWalletState]  = useState(null);
  const [needsBackup, setNeedsBackup] = useState(false);
  const [isLoading, setIsLoading]     = useState(false);
  const [step, setStep]               = useState('create');

  const persist = useCallback((data) => {
    localStorage.setItem('ikhuaWallet', JSON.stringify(data));
    setWalletState(data);
  }, []);

  /* Silently sync emailVerified + kycStatus from server, and verify session */
  const syncRemoteState = useCallback(async (current) => {
    if (!current?.email) return;

    // Session validity check — force logout if password changed elsewhere
    if (current.sessionToken) {
      try {
        const { data: sv } = await apiClient.get(
          `/wallet/verify-session?email=${encodeURIComponent(current.email)}&token=${encodeURIComponent(current.sessionToken)}`
        );
        if (!sv.valid) {
          localStorage.removeItem('ikhuaWallet');
          setWalletState(null);
          setNeedsBackup(false);
          setStep('create');
          toast.warning('Signed out — your password was changed on another device.');
          return;
        }
      } catch { /* silent — on error don't force logout */ }
    }

    let updated = { ...current };
    let changed = false;

    if (!current.emailVerified) {
      try {
        const { data } = await apiClient.get(
          `/wallet/verification-status?email=${encodeURIComponent(current.email)}`
        );
        if (data.emailVerified) { updated.emailVerified = true; changed = true; }
      } catch { /* silent */ }
    }

    if (current.kycStatus !== 'approved') {
      try {
        const { data } = await apiClient.get(
          `/kyc/status?email=${encodeURIComponent(current.email)}`
        );
        if (data.status && data.status !== current.kycStatus) {
          updated.kycStatus          = data.status;
          updated.kycRejectionReason = data.rejectionReason ?? null;
          changed = true;
        }
      } catch { /* silent */ }
    }

    if (changed) persist(updated);
  }, [persist]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Rehydrate on mount */
  useEffect(() => {
    const saved = localStorage.getItem('ikhuaWallet');
    if (saved) {
      const parsed = JSON.parse(saved);
      setWalletState(parsed);
      setNeedsBackup(!parsed.backedUp);
      setStep('wallet');
      syncRemoteState(parsed);
    }
  }, [syncRemoteState]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const saved = localStorage.getItem('ikhuaWallet');
      if (!saved) return;
      syncRemoteState(JSON.parse(saved));
    };
    const onPageShow = (e) => {
      if (e.persisted || document.visibilityState === 'visible') {
        const saved = localStorage.getItem('ikhuaWallet');
        if (!saved) return;
        syncRemoteState(JSON.parse(saved));
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [syncRemoteState]);

  const walletRef = useRef(null);
  useEffect(() => { walletRef.current = wallet; }, [wallet]);

  const prevKycRef = useRef(null);
  useEffect(() => {
    if (!wallet) return;
    const prev = prevKycRef.current;
    const curr = wallet.kycStatus;
    if (prev && prev !== curr) {
      if (curr === 'approved') toast.success('KYC approved! You now have full P2P access.');
      if (curr === 'rejected') toast.error('KYC not approved. Check your profile for details.');
    }
    prevKycRef.current = curr;
  }, [wallet?.kycStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (wallet?.kycStatus !== 'pending') return;
    const id = setInterval(() => {
      if (walletRef.current) syncRemoteState(walletRef.current);
    }, 45_000);
    return () => clearInterval(id);
  }, [wallet?.kycStatus, syncRemoteState]);

  /* ── NEW: Register custodial account ───────────────────────
     No seed phrase. Backend derives wallet from PLATFORM_MNEMONIC.
  ── */
  const registerWallet = async (email, password, displayName = '') => {
    setIsLoading(true);
    try {
      const deviceFingerprint = await getBrowserFingerprint();
      const { data } = await apiClient.post('/wallet/register', { email, password, displayName, deviceFingerprint });
      const walletData = {
        custodial:     true,
        backedUp:      true,
        emailVerified: false,
        kycStatus:     'none',
        email:         data.email,
        displayName:   data.displayName,
        btcAddress:    data.wallets.btc.address,
        trxAddress:    data.wallets.trx.address,
        ethAddress:    data.wallets.bnb?.address ?? null,
        wallets:       data.wallets,
        createdAt:     new Date().toISOString(),
      };
      persist(walletData);
      setNeedsBackup(false);
      setStep('wallet');
    } finally {
      setIsLoading(false);
    }
  };

  /* ── LEGACY: Create non-custodial wallet (still used for guest mode) */
  const createWallet = async () => {
    setIsLoading(true);
    try {
      const { data } = await apiClient.post('/wallet/create');
      const walletData = {
        custodial:     false,
        backedUp:      false,
        emailVerified: false,
        kycStatus:     'none',
        mnemonic:      data.mnemonic,
        wallets:       data.wallets,
        btcAddress:    data.wallets.btc.address,
        trxAddress:    data.wallets.trx.address,
        ethAddress:    data.wallets.eth.address,
        createdAt:     new Date().toISOString(),
      };
      persist(walletData);
      setNeedsBackup(true);
      setStep('mnemonic');
      toast.success('Wallet created!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Wallet creation failed');
    } finally {
      setIsLoading(false);
    }
  };

  /* ── LEGACY: Backup non-custodial wallet */
  const backupWallet = async (email, password, displayName = '') => {
    const eth = wallet?.wallets?.eth?.address;
    const btc = wallet?.wallets?.btc?.address;
    const trx = wallet?.wallets?.trx?.address;
    if (!wallet?.mnemonic || !eth || !btc || !trx) {
      throw new Error('Wallet data is incomplete. Please delete this wallet and create a new one.');
    }
    const encryptedMnemonic = await encryptMnemonic(wallet.mnemonic, password, email);
    const { data } = await apiClient.post('/wallet/backup', {
      email, password, displayName, encryptedMnemonic,
      ethAddress: eth, btcAddress: btc, trxAddress: trx,
    });
    if (data.success) {
      const updated = {
        ...wallet,
        custodial:     false,
        backedUp:      true,
        emailVerified: false,
        email:         data.user.email ?? email,
        displayName:   data.user.displayName ?? displayName,
        backedUpAt:    data.user.backedUpAt,
      };
      persist(updated);
      setNeedsBackup(false);
      setStep('wallet');
      toast.success('Wallet backed up!');
    }
  };

  /* ── Recover via email + password (works for both custodial and legacy) */
  const recoverWallet = async (email, password) => {
    const { data } = await apiClient.post('/wallet/recover', { email, password });

    if (data.success) {
      let mnemonic = null;
      if (!data.custodial && data.encryptedMnemonic) {
        try {
          mnemonic = await decryptMnemonic(data.encryptedMnemonic, password, email);
        } catch {
          throw new Error('Could not decrypt wallet — please check your password.');
        }
      }

      const walletData = {
        custodial:     data.custodial ?? false,
        backedUp:      true,
        emailVerified: data.emailVerified ?? false,
        kycStatus:     data.kycStatus ?? 'none',
        kycRejectionReason: data.kycRejectionReason ?? null,
        phone:         data.phone ?? null,
        email:         data.email ?? email,
        displayName:   data.displayName ?? '',
        sessionToken:  data.sessionToken ?? null,
        mnemonic,
        legacyAccount: data.legacyAccount ?? false,
        wallets:       data.wallets,
        btcAddress:    data.wallets.btc.address,
        trxAddress:    data.wallets.trx.address,
        ethAddress:    data.wallets.bnb?.address ?? data.wallets.eth?.address ?? null,
        recoveredAt:   new Date().toISOString(),
      };
      persist(walletData);
      setNeedsBackup(false);
      setStep('wallet');
      toast.success('Welcome back!');
    }
  };

  /* ── LEGACY: Recover via 12-word phrase */
  const recoverByMnemonic = async (mnemonic) => {
    const { data } = await apiClient.post('/wallet/recover-mnemonic', { mnemonic });
    if (data.success) {
      const walletData = {
        custodial:     false,
        backedUp:      false,
        emailVerified: false,
        mnemonic,
        wallets:       data.wallets,
        btcAddress:    data.wallets.btc.address,
        trxAddress:    data.wallets.trx.address,
        ethAddress:    data.wallets.eth.address,
        recoveredAt:   new Date().toISOString(),
      };
      persist(walletData);
      setNeedsBackup(true);
      setStep('wallet');
      toast.success('Wallet imported!');
    }
  };

  const confirmMnemonicSeen = () => setStep('backup');

  const skipBackup = () => {
    const updated = { ...wallet, backedUp: false };
    persist(updated);
    setNeedsBackup(true);
    setStep('wallet');
    toast.info('You can back up later in Profile');
  };

  const signOutWallet = () => {
    localStorage.removeItem('ikhuaWallet');
    setWalletState(null);
    setNeedsBackup(false);
    setStep('create');
    toast.info('Signed out');
  };

  const setWallet = (data) => {
    if (data) persist(data);
    else setWalletState(null);
  };

  /* ── Change password */
  const changePassword = async (currentPassword, newPassword) => {
    const { email, mnemonic, custodial } = wallet ?? {};
    if (!email) throw new Error('No email on file — sign in again.');

    let newEncryptedMnemonic;
    if (!custodial && mnemonic) {
      newEncryptedMnemonic = await encryptMnemonic(mnemonic, newPassword, email);
    }

    const { data } = await apiClient.post('/wallet/change-password', {
      email, currentPassword, newPassword,
      ...(newEncryptedMnemonic ? { newEncryptedMnemonic } : {}),
    });
    if (data.sessionToken) persist({ ...wallet, sessionToken: data.sessionToken });
    toast.success('Password updated successfully');
  };

  /* ── Forgot password
     Custodial: OTP-based — caller must pass { email, otpCode, newPassword }
     Legacy: seed phrase proof — caller must pass { email, phrase, newPassword }
  */
  const forgotPassword = async (email, phraseOrOtpCode, newPassword, isCustodial = false) => {
    if (isCustodial) {
      await apiClient.post('/wallet/forgot-password', { email, otpCode: phraseOrOtpCode, newPassword });
    } else {
      const seedPhrase = phraseOrOtpCode;
      const derived    = deriveAddressesFromMnemonic(seedPhrase.trim().toLowerCase());
      const newEncryptedMnemonic = await encryptMnemonic(seedPhrase.trim().toLowerCase(), newPassword, email);
      await apiClient.post('/wallet/forgot-password', {
        email, newPassword, newEncryptedMnemonic,
        derivedBtcAddress: derived.btc,
        derivedEthAddress: derived.eth,
        derivedTrxAddress: derived.trx,
      });
    }
    toast.success('Password reset — sign in with your new password');
  };

  return (
    <WalletContext.Provider value={{
      wallet, needsBackup, isLoading, step,
      registerWallet,
      createWallet, backupWallet, recoverWallet,
      recoverByMnemonic, confirmMnemonicSeen,
      skipBackup, signOutWallet,
      changePassword, forgotPassword,
      setStep, setWallet, setNeedsBackup, syncRemoteState,
    }}>
      {children}
    </WalletContext.Provider>
  );
}
